import { CoreLogService } from "./log.service";
import { CoreCOMService } from "./com.service";
import { Request, IHttpService } from "./http.service";
import { environment } from "../../../environments/environment";
import { SafeFileSystem } from "./safeFileSystem.service";
import { EnvDir, FileSystemDefault } from "./com/comEnvironmentFileSystem";
import Utils from "../shared/utils";
import * as moment from "moment";
import { appConfig } from "../core-config";
import {
  ContentType,
  GetContentDirFromType,
  ImageOrientation,
  ImageWidthSizes,
  YourAPPProductType,
} from "../shared/contentTypes";
import { Observable, Subscriber } from "rxjs";
/** UAPP Image Access Service*/
export class CoreImageService {
  // urls zu den standard bildern
  public static get loadingImageURL(): string {
    return CoreImageService.getImageURL("image_loading.svg");
  }
  public static get errorImageURL(): string {
    return CoreImageService.getImageURL("image_error.svg");
  }

  private static getImageURL(imageName: string) {
    return FileSystemDefault.CreatePathForFile("core/img/" + imageName, EnvDir.Assets);
  }

  /** static cache for showed images (it reduce the time for checking the local files)
   * nur lokal gehalten, wird nach dem neustart der App neu zusammengestellt
   */
  private static _imageCache: { [key: string]: string } = {};

  /** Im Speicher vorgehaltene Dateien
   * Wird genutzt um den Browser Zugriff auf Bilder im EnvDir.Data zu gewähren.
   * (Seit Android API 30 notwendig)
   */
  private static _blobCache: { [blobURL: string]: Date } = {};

  /** Hält die letzte Zuweisung von fileURL zu BlobURL.
   * Muss mit dem _blobCache crossverifiziert werden, ob es noch gültig ist.
   */
  private static _fileURLToBlobURLCache: { [fileURL: string]: string } = {};

  /** Intervall in MilliSekunden nach dem ungenutzte Blob Referenzen entfernt werden,
   * um sie für den Garbage Collector freizugeben
   */
  private static _blobCacheCleanupTime: number = 60 * 1000; // 1 Min;

  /** MilliSekunden nach dem eine BlobReferenz als abgelaufen gilt */
  private static _blobCacheExpirationTime: number = 60 * 1000; // 1 Min

  /** Sekunden nach dem eine BlobReferenz als abgelaufen gilt */
  private static _blobCacheCleanupInterval = null;

  /** Sollen BilderZugriffe auf EnvDir.Data als inMemoryBlobReferenzen zurückgegeben werden */
  public static EnvDirDataImagesAsInMemory = true;

  /** UAPP Image Access Service */
  constructor(
    private _coreCOMService: CoreCOMService,
    private _safeFileSystem: SafeFileSystem,
    private _coreLogService: CoreLogService,
    private _httpService: IHttpService
  ) {
    const me = this;
    if (me._coreCOMService.environment.client.isMobile) {
      // Regularly cleanup BlobCache on Mobile
      if (CoreImageService._blobCacheCleanupInterval == null) {
        CoreImageService._blobCacheCleanupInterval = setInterval(() => {
          const deleteIfOlderThan = new Date(
            Date.now() - CoreImageService._blobCacheExpirationTime
          );
          for (const blobURL in CoreImageService._blobCache) {
            if (CoreImageService._blobCache[blobURL] < deleteIfOlderThan) {
              window.URL.revokeObjectURL(blobURL);
              delete CoreImageService._blobCache[blobURL];
            }
          }
        }, CoreImageService._blobCacheCleanupTime);
      }
    }
  }

  /**
   * Gets a Url to get the local Image.
   * If no local Image then it returns "".
   * @param path z.B. "0/id_name_orientation/20010101/0"
   */
  public getLocalURLByPath(path: string): Promise<string> {
    const me = this;
    return new Promise<string>((resolve, reject) => {
      const paths = path.split("/");
      const contentType = parseInt(paths[0], 10);
      const imageKey = paths[1];
      const contentDir = GetContentDirFromType(contentType);
      const noLocalURL = "";

      if (me._coreCOMService.environment.client.isMobile) {
        me._safeFileSystem
          .getFile(EnvDir.Data, "images/" + contentDir, imageKey)
          .then((fileEntryData) => {
            if (fileEntryData != null) {
              this.getBlobURLForFileEntry(fileEntryData)
                .then((blobURL) => {
                  resolve(blobURL);
                })
                .catch(() => {
                  resolve(fileEntryData.toURL());
                });
            } else {
              me._safeFileSystem
                .getFile(EnvDir.Assets, "images/" + contentDir, imageKey)
                .then((fileEntryAssets) => {
                  if (fileEntryAssets != null) {
                    resolve(FileSystemDefault.ConvertPath(fileEntryAssets.toURL()));
                  } else {
                    resolve(noLocalURL);
                  }
                })
                .catch((assetsError) => resolve(noLocalURL));
            }
          })
          .catch((dataError) => {
            me._safeFileSystem
              .getFile(EnvDir.Assets, "images/" + contentDir, imageKey)
              .then((fileEntryAssets) => {
                if (fileEntryAssets != null) {
                  resolve(fileEntryAssets.toURL());
                } else {
                  resolve(noLocalURL);
                }
              })
              .catch((assetsError) => resolve(noLocalURL));
          });
      } else {
        resolve(noLocalURL);
      }
    });
  }

  /**
   * Baut den Image key auf
   * @param content_id
   * @param image_name
   * @param image_orientation siehe ImageOrientation
   */
  public static CreateImageKey(
    content_id: number | string,
    image_name: string,
    image_orientation: ImageOrientation
  ): string {
    return content_id + "_" + image_name + "_" + image_orientation;
  }

  /** Gets a Url to get the Image (it try to read image from local storage)
   * @param path z.B. "0/id_name_orientation/20010101/0"
   * @param ignore_assets ignoriert das in den Assets mitgelieferte bild und versucht es herunterzuladen
   * @param image_size image_size (-1 auto/ignore), if set it is ImageWidthSizes.Size[image_size] and used to load image from assets if device size is okay
   */
  public getURLByPath(
    path: string,
    ignore_assets: boolean = false,
    image_size: number = -1
  ): Observable<string> {
    const me = this;
    const paths = path.split("/");
    const contentType = parseInt(paths[0], 10);
    const imageKey = paths[1];
    const image_change_at_ymd = parseInt(paths[2], 10);
    const image_change_at_hms = parseInt(paths[3], 10);
    // overwrite image_size if it is not set
    if (image_size === -1 && paths.length > 4) {
      image_size = parseInt(paths[4], 10);
    }
    return me.getURL(
      contentType,
      imageKey,
      image_change_at_ymd,
      image_change_at_hms,
      ignore_assets,
      image_size
    );
  }

  /** Gets a Url to get the Image (it try to read image from local storage)
   * @param contentType z.B. 0 = "news"
   * @param imageKey z.B. "id_name_orientation"
   * @param image_change_at_ymd image_change_at_ymd + image_change_at_hms zusammen zeigen an (wenn größer als write image date) das neue heruntergeladen werden sollen
   * @param image_change_at_hms image_change_at_ymd + image_change_at_hms zusammen zeigen an (wenn größer als write image date) das neue heruntergeladen werden sollen
   * @param ignore_assets ignoriert das in den Assets mitgelieferte bild und versucht es herunterzuladen
   * @param image_size image_size (-1 auto/ignore), if set it is ImageWidthSizes.SizeX and used to load image from assets if device size is okay
   */
  public getURL(
    contentType: ContentType,
    imageKey: string,
    image_change_at_ymd: number,
    image_change_at_hms: number,
    ignore_assets: boolean = false,
    image_size: number = -1
  ): Observable<string> {
    const me = this;
    const contentDir = GetContentDirFromType(contentType);
    const cachedImageKey =
      contentType +
      "_" +
      imageKey +
      "_" +
      image_change_at_ymd +
      "_" +
      image_change_at_hms +
      "_" +
      image_size +
      "_" +
      ignore_assets;
    let cachedImageUri = CoreImageService._imageCache[cachedImageKey];
    if (cachedImageUri && cachedImageUri.startsWith("blob")) {
      const cachedBlobLastAcess = CoreImageService._blobCache[cachedImageUri];
      if (cachedBlobLastAcess != null) {
        CoreImageService._blobCache[cachedImageUri] = new Date();
      } else {
        cachedImageUri = null;
      }
    }

    const GetImageUri = (): string => {
      // ermitele die gewünschte bild größe (etweder die übergebene oder die für das gerät optimale)
      let width: number = 0;
      if (image_size != -1) {
        width = ImageWidthSizes["Size" + image_size]; // z.B. ImageWidthSizes.Size3
        if (width === 0) {
          me._coreLogService.error(
            "CoreImageService.GetImageUri",
            "unknown image_size",
            image_size
          );
        }
      }
      if (width === 0) {
        // Zurückfallen auf Viewport Breite
        if (this._coreCOMService.environment.client.isMobile) {
          width = Utils.GetViewInnerWidth(); // oder GetPhysicalScreenWidth damit hätte man die mögliche größe des Browsers
        } else {
          width = Utils.GetAPPLogicalWidth(ImageWidthSizes.Size3); // Browser ist limitiert durch APPBreite (ohne möglichen Rand)
        }
      }

      const RequestUri =
        "?type=" +
        contentType +
        "&file=" +
        encodeURIComponent(imageKey) +
        "&width=" +
        encodeURIComponent(width.toString()) +
        "&v=" +
        image_change_at_ymd +
        "_" +
        image_change_at_hms; // version, damit es auch neu geladen wird und nicht der der browser cache
      return environment.ImageServerURI + RequestUri;
    };

    const SetImageUri = (
      observer: Subscriber<string>,
      newImageUri: string,
      cacheNewImage: boolean
    ) => {
      // chenge only if the image is a other
      if (newImageUri != cachedImageUri) {
        // cache the new uri
        if (cacheNewImage) {
          CoreImageService._imageCache[cachedImageKey] = newImageUri;
        }
        observer.next(newImageUri);
      }
    };

    const TryDownloadImageAndSafe = (
      observer: Subscriber<string>,
      default_uri: string,
      suppress_loading_image: boolean = false
    ) => {
      // if has internet try download the image, else return the default url
      if (!me._coreCOMService.environment.networkInformation.isOnline) {
        me._coreLogService.error(
          "CoreImageService.getURL",
          "no internet",
          GetImageUri()
        );
        // wenn der abruf in das internet gehen soll, durch ein fehler image ersetzen
        if (default_uri.startsWith(environment.ImageServerURI)) {
          SetImageUri(observer, default_uri, false); // ohne internet weiter die uri verwenden, es gibt ein onerror zur absicherung
          observer.complete();
        } else {
          SetImageUri(observer, default_uri, false);
          observer.complete();
        }
      } else {
        // show loading image by downloading
        if (!suppress_loading_image) {
          observer.next(CoreImageService.loadingImageURL);
        }
        const downloadRequest = new Request({
          baseURI: GetImageUri(),
          uri: "",
          method: "GET",
          headers: null, // no headers, width is in GetImageUri()
          body: null, // no body is a GET
          responseType: "blob", // get image as Blob
        });
        // lade das Bild herunter, und speicher es local (und verwende die lokale URL), sonst link zum Server als Fallback?
        me._httpService
          .Request(downloadRequest)
          .then((response) => {
            if (response.ok) {
              // momentan wird isOptimal nicht genutzt, auch bilder in der falschen größe werden gespeichert
              // sollte ein bild in einer anderen größe gespeichert werden erfolgt eine neue Anfrage automatisch
              //const isOptimal = (response.headers["rightsize"] === "1"); // bild hat die angefragte größe?
              me._safeFileSystem
                .createFile(EnvDir.Data, "images/" + contentDir, imageKey)
                .then((fileEntry) => {
                  me._safeFileSystem
                    .writeToFileByEntry(fileEntry, response.body, false)
                    .then(() => {
                      if (CoreImageService.EnvDirDataImagesAsInMemory) {
                        const blob = response.body;
                        const blobURL = me.createObjectURL(blob);
                        SetImageUri(observer, blobURL, true);
                      } else {
                        SetImageUri(observer, fileEntry.toURL(), true);
                      }
                      observer.complete();
                    })
                    .catch(() => {
                      SetImageUri(observer, GetImageUri(), false);
                      observer.complete();
                    });
                });
            } else {
              SetImageUri(observer, GetImageUri(), false);
              observer.complete();
            }
          })
          .catch((error) => {
            me._coreLogService.error(
              "CoreImageService.getURL",
              "HttpService.Request",
              downloadRequest,
              error
            );
            SetImageUri(observer, GetImageUri(), false);
            observer.complete();
          });
      }
    };

    return new Observable<string>((observer) => {
      // use cached image if exists
      if (cachedImageUri) {
        observer.next(cachedImageUri);
        if (cachedImageUri.startsWith("file://")) {
          observer.complete(); // it is a local image, deeper search is not need
          return; // break, deeper searching
        }
      }

      // Mobile Device can storage the images, Browser needs get them by self
      if (me._coreCOMService.environment.client.isMobile) {
        me._safeFileSystem
          .getFile(EnvDir.Data, "images/" + contentDir, imageKey)
          .then((fileEntryData) => {
            if (fileEntryData != null) {
              // check image is actual
              fileEntryData.getMetadata(
                (metadata) => {
                  const image_change_at = Utils.GetMomentFromInt(
                    image_change_at_ymd,
                    image_change_at_hms
                  );
                  const file_create_at = moment(
                    metadata.modificationTime
                  ).minutes(-10); // about 10 minutes the YourappServer is need to download the images

                  this.getBlobURLForFileEntry(fileEntryData).then((blobURL) => {
                    if (image_change_at < file_create_at) {
                      SetImageUri(observer, blobURL, true);
                      observer.complete();
                    } else {
                      SetImageUri(observer, blobURL, false);
                      TryDownloadImageAndSafe(observer, blobURL, true); // suppress loading image, show storaged image in this time
                    }
                  });
                },
                (error) => {
                  me._coreLogService.error(
                    "CoreImageService.getURL",
                    "fileEntryData.getMetadata",
                    fileEntryData.fullPath,
                    error
                  );
                  SetImageUri(observer, fileEntryData.toURL(), false);
                }
              );
            } else {
              me._safeFileSystem
                .getFile(EnvDir.Assets, "images/" + contentDir, imageKey)
                .then((fileEntryAssets) => {
                  if (fileEntryAssets != null) {
                    // check assets image is actual (against creationDate of the APP)
                    const image_change_at = Utils.GetMomentFromInt(
                      image_change_at_ymd,
                      image_change_at_hms
                    );
                    if (image_change_at <= appConfig.created_at()) {
                      SetImageUri(
                        observer,
                        FileSystemDefault.ConvertPath(fileEntryAssets.toURL()),
                        !ignore_assets
                      );
                      if (ignore_assets) {
                        // wenn die assets zu ignorieren sind, wird ein versuch unternommen ein pasenderes Bild herunterzuladen
                        if (
                          image_size > -1 &&
                          Utils.GetViewInnerWidth() <=
                          ImageWidthSizes["Size" + image_size]
                        ) {
                          observer.complete(); // ist die devicesize kleiner als image_size in den assets, können die assets verwendet werden
                        } else {
                          TryDownloadImageAndSafe(
                            observer,
                            fileEntryAssets.toURL(),
                            true
                          ); // suppress loading image, show storaged image in this time
                        }
                      } else {
                        observer.complete();
                      }
                    } else {
                      SetImageUri(observer, fileEntryAssets.toURL(), false);
                      TryDownloadImageAndSafe(
                        observer,
                        fileEntryAssets.toURL(),
                        true
                      ); // suppress loading image, show storaged image in this time
                    }
                  } else {
                    // image not local gets from imagewebserver
                    TryDownloadImageAndSafe(observer, GetImageUri());
                  }
                })
                .catch((error) => {
                  me._coreLogService.error(
                    "CoreImageService.getURL",
                    "getFile/Assets",
                    error
                  );
                  observer.error();
                });
            }
          })
          .catch((error) => {
            me._coreLogService.error(
              "CoreImageService.getURL",
              "getFile/Data",
              error
            );
            observer.error();
          });
      } else {
        SetImageUri(observer, GetImageUri(), true);
        observer.complete();
      }
    });
  }

  private getBlobURLForFileEntry(fileEntryData: FileEntry): Promise<string> {
    const me = this;
    return new Promise<string>((resolve, reject) => {
      const fileURL = fileEntryData.toURL();
      if (CoreImageService.EnvDirDataImagesAsInMemory) {
        const cachedBlobURL = CoreImageService._fileURLToBlobURLCache[fileURL];
        if (cachedBlobURL && CoreImageService._blobCache[cachedBlobURL]) {
          CoreImageService._blobCache[cachedBlobURL] = new Date();
          resolve(cachedBlobURL);
        } else {
          fileEntryData.file((file) => {
            const reader = new FileReader();
            reader.onloadend = function () {
              const blob = new Blob(
                [new Uint8Array(this.result as ArrayBuffer)],
                { type: file.type }
              );
              const blobURL = me.createObjectURL(blob);
              CoreImageService._fileURLToBlobURLCache[fileURL] = blobURL;
              resolve(blobURL);
            };
            reader.onabort = function () {
              reject("FileReader.onabort");
            };
            reader.onerror = function () {
              reject("FileReader.onerror");
            };
            reader.readAsArrayBuffer(file);
          });
        }
      } else {
        resolve(fileURL);
      }
    });
  }

  private createObjectURL(blob: Blob): string {
    const blobURL = window.URL.createObjectURL(blob);
    CoreImageService._blobCache[blobURL] = new Date();
    return blobURL;
  }
}
